import { formatSnakeStringWithCapitalization } from '@/utils/string_utils'
import {
  DOPPEL_TEXT_BLACK,
  FONT_SIZE_SMALL,
  FONT_SIZE_MEDIUM,
  DOPPEL_WHITE_NEW,
  DOPPEL_NODE_RED,
} from '@/utils/style'
import { AddIcon } from '@chakra-ui/icons'
import {
  Box,
  CloseButton,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  Spacer,
  Stack,
  Text,
  PopoverAnchor,
  VStack,
  Center,
  HStack,
} from '@chakra-ui/react'
import {
  ThreatGraphLink,
  ThreatGraphLinkExtended,
  ThreatGraphNodeExtended,
} from '@/components/threat_graph/threat_graph_utils'
import AlertIdLink from '../doppel_design/alert_id_link'
import { useIsEmployeeView } from '@/hooks/id_token_claims'

const NODE_POPOVER_OFFSET = 5 // graph coords, in case we need zoom

type ThreatGraphPopoverInput = {
  graphRef: React.RefObject<any>
  baseX: number // in graph coordinates; will be converted to screen coordinates
  baseY: number
  bgColor: string
  textColor: string
  useNodeOffset?: boolean
  children: React.ReactNode
}

// base popover for use in other classes
const ThreatGraphPopover = ({
  graphRef,
  baseX,
  baseY,
  bgColor,
  textColor,
  useNodeOffset = false,
  children,
}: ThreatGraphPopoverInput) => {
  if (!graphRef.current?.graph2ScreenCoords) {
    return null
  }

  // only updates position on render, but that's fine for now (will be a bit buggy while graph is moving)
  const offset = useNodeOffset ? NODE_POPOVER_OFFSET : 0
  const { x, y } = graphRef.current.graph2ScreenCoords(baseX, baseY - offset)

  return (
    <Popover isOpen={true} placement="top">
      <PopoverAnchor>
        <Box
          height="1"
          left={x - 1} // offset by 1 for centering
          position="absolute"
          style={{ visibility: 'hidden' }}
          top={y - 1}
          width="1"
        />
      </PopoverAnchor>

      <PopoverContent
        bg={bgColor}
        borderRadius="md"
        boxShadow="md"
        cursor="default"
        height="fit-content"
        textColor={textColor}
        width="fit-content"
      >
        <PopoverArrow bg={bgColor} />

        <PopoverBody pb={1.5} pt={1}>
          {children}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}

type NodeHoverPopoverInput = {
  node: ThreatGraphNodeExtended // react-force-graph-2d node data, not original data
  graphRef: React.RefObject<any>
}

export const NodeHoverPopover = ({ node, graphRef }: NodeHoverPopoverInput) => {
  const bgColor = DOPPEL_WHITE_NEW
  const textColor = DOPPEL_TEXT_BLACK

  return (
    <ThreatGraphPopover
      baseX={node.x}
      baseY={node.y}
      bgColor={bgColor}
      graphRef={graphRef}
      textColor={textColor}
      useNodeOffset
    >
      <VStack fontSize={FONT_SIZE_SMALL} fontWeight={600} spacing={0}>
        <Text isTruncated maxW="200px">
          {node.entity.value}
        </Text>

        <Text>
          {formatSnakeStringWithCapitalization(
            node.entity.subtypeMetadata?.name || node.entity.archetype,
          )}
        </Text>

        {node.currentNode && <Text>(current)</Text>}
      </VStack>
    </ThreatGraphPopover>
  )
}

type LinkDetailPopoverInput = {
  link: ThreatGraphLinkExtended // react-force-graph-2d link data, not original data
  graphRef: React.RefObject<any>
  onClose: () => void
  openDeleteModal: (link: ThreatGraphLink) => void
}

export const LinkDetailPopover = ({
  link,
  graphRef,
  onClose,
  openDeleteModal,
}: LinkDetailPopoverInput) => {
  const bgColor = DOPPEL_WHITE_NEW
  const textColor = DOPPEL_TEXT_BLACK
  const [isEmployeeView] = useIsEmployeeView()

  if (!link.source || !link.target) {
    return null
  }
  const midX = (link.source.x + link.target.x) / 2
  const midY = (link.source.y + link.target.y) / 2
  return (
    <ThreatGraphPopover
      baseX={midX}
      baseY={midY}
      bgColor={bgColor}
      graphRef={graphRef}
      textColor={textColor}
    >
      <Stack maxHeight="300px" overflowY="auto" pl={1} py={1.5} spacing={1}>
        <Stack direction="row">
          <Text
            fontSize={FONT_SIZE_MEDIUM}
            fontWeight="bold"
            maxWidth="200px"
            overflowY="auto"
          >
            {formatSnakeStringWithCapitalization(link.linkType)}
          </Text>

          <Spacer />

          <CloseButton boxSize={3} onClick={onClose} />
        </Stack>

        <HStack spacing={0}>
          <Text fontSize={FONT_SIZE_SMALL} fontWeight="bold" mr={0.5}>
            Parent:
          </Text>

          <Text fontSize={FONT_SIZE_SMALL} isTruncated maxW="200px">
            {link.source.entity.value}
          </Text>
        </HStack>

        <HStack spacing={0}>
          <Text fontSize={FONT_SIZE_SMALL} fontWeight="bold" mr={0.5}>
            Child:
          </Text>

          <Text fontSize={FONT_SIZE_SMALL} isTruncated maxW="200px">
            {link.target.entity.value}
          </Text>
        </HStack>

        {isEmployeeView && (
          <Center>
            <Text
              color={DOPPEL_NODE_RED}
              cursor="pointer"
              fontSize={FONT_SIZE_SMALL}
              fontWeight="bold"
              onClick={() => openDeleteModal(link)}
            >
              Delete Link
            </Text>
          </Center>
        )}
      </Stack>
    </ThreatGraphPopover>
  )
}

type NodeDetailPopoverInput = {
  node: ThreatGraphNodeExtended // react-force-graph-2d node data, not original data
  graphRef: React.RefObject<any>
  addModalAction?: () => void
  onClose: () => void
}

export const NodeDetailPopover = ({
  node,
  graphRef,
  addModalAction,
  onClose,
}: NodeDetailPopoverInput) => {
  const bgColor = DOPPEL_WHITE_NEW
  const textColor = DOPPEL_TEXT_BLACK

  return (
    <ThreatGraphPopover
      baseX={node.x}
      baseY={node.y}
      bgColor={bgColor}
      graphRef={graphRef}
      textColor={textColor}
      useNodeOffset
    >
      <Stack maxHeight="300px" overflowY="auto" pl={1} py={1.5} spacing={1}>
        <Stack direction="row">
          <Text
            fontSize={FONT_SIZE_MEDIUM}
            fontWeight="bold"
            maxWidth="200px"
            overflowY="auto"
          >
            {node.entity.value}
          </Text>

          <Spacer />

          {addModalAction && (
            <IconButton
              aria-label="Add"
              boxSize={3}
              color={DOPPEL_TEXT_BLACK}
              icon={<AddIcon boxSize={3} />}
              onClick={addModalAction}
              size="xs"
            />
          )}

          <CloseButton boxSize={3} onClick={onClose} />
        </Stack>

        <Text fontSize={FONT_SIZE_SMALL} fontWeight="bold">
          {node.alert?.id && (
            <AlertIdLink alertId={node.alert.id} color={DOPPEL_TEXT_BLACK} />
          )}

          {formatSnakeStringWithCapitalization(
            node.entity.subtypeMetadata?.name || node.entity.archetype,
          )}
        </Text>
      </Stack>
    </ThreatGraphPopover>
  )
}

type LinkHoverPopoverInput = {
  link: ThreatGraphLinkExtended // react-force-graph-2d link data, not original data
  graphRef: React.RefObject<any>
}

export const LinkHoverTooltip = ({ link, graphRef }: LinkHoverPopoverInput) => {
  // Calculate the midpoint of the line
  if (!link.source || !link.target) {
    return null
  }
  const midX = (link.source.x + link.target.x) / 2
  const midY = (link.source.y + link.target.y) / 2
  return (
    <ThreatGraphPopover
      baseX={midX}
      baseY={midY}
      bgColor={DOPPEL_WHITE_NEW}
      graphRef={graphRef}
      textColor={DOPPEL_TEXT_BLACK}
    >
      <Box
        fontSize={FONT_SIZE_SMALL}
        fontWeight={600}
        maxWidth="200px"
        textAlign="center"
        whiteSpace="nowrap"
      >
        {formatSnakeStringWithCapitalization(link.linkType)}
      </Box>
    </ThreatGraphPopover>
  )
}
